import { ThemeConfig } from '../../types/organization';

/**
 * Default theme configuration for the chat widget.
 * These values are used as defaults when an organization hasn't customized their theme,
 * and for resetting theme settings to defaults.
 */
export const DEFAULT_CHAT_WIDGET_THEME: ThemeConfig = {
  // Primary colors - based on application's primary color scheme
  primaryColor: '#6366f1', // Main brand color (Indigo)
  primaryColorLight: '#818cf8', // Lighter variant for hover states
  primaryColorDark: '#4f46e5', // Darker variant for active states

  // Accent color for CTAs and highlights
  accentColor: '#10b981', // Emerald green for action buttons

  // Background and text colors
  backgroundColor: '#ffffff', // Chat background
  textColor: '#1f2937', // Primary text (dark gray)
  textColorLight: '#6b7280', // Secondary/muted text

  // Chat bubble colors
  bubbleUserBg: '#6366f1', // User message bubble (matches primary)
  bubbleAgentBg: '#f3f4f6', // Agent message bubble (light gray)

  // Border radius style
  borderRadius: 'rounded', // Options: 'rounded' | 'soft' | 'sharp'
};

/**
 * Predefined theme collection for chat widget customization.
 * Users can select from these themes or choose "Custom" for manual configuration.
 */
export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  config: ThemeConfig;
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'cacxi',
    name: 'CACXI',
    description: 'Default theme with modern indigo tones',
    config: {
      primaryColor: '#6366f1',
      primaryColorLight: '#818cf8',
      primaryColorDark: '#4f46e5',
      accentColor: '#10b981',
      backgroundColor: '#ffffff',
      textColor: '#1f2937',
      textColorLight: '#6b7280',
      bubbleUserBg: '#6366f1',
      bubbleAgentBg: '#f3f4f6',
      borderRadius: 'rounded',
    },
  },
  {
    id: 'ocean',
    name: 'Ocean Blue',
    description: 'Calm and trustworthy blue tones',
    config: {
      primaryColor: '#0ea5e9',
      primaryColorLight: '#38bdf8',
      primaryColorDark: '#0284c7',
      accentColor: '#f59e0b',
      backgroundColor: '#ffffff',
      textColor: '#0f172a',
      textColorLight: '#64748b',
      bubbleUserBg: '#0ea5e9',
      bubbleAgentBg: '#f0f9ff',
      borderRadius: 'rounded',
    },
  },
  {
    id: 'emerald',
    name: 'Emerald',
    description: 'Fresh and natural green palette',
    config: {
      primaryColor: '#10b981',
      primaryColorLight: '#34d399',
      primaryColorDark: '#059669',
      accentColor: '#8b5cf6',
      backgroundColor: '#ffffff',
      textColor: '#1e293b',
      textColorLight: '#64748b',
      bubbleUserBg: '#10b981',
      bubbleAgentBg: '#ecfdf5',
      borderRadius: 'rounded',
    },
  },
  {
    id: 'sunset',
    name: 'Sunset',
    description: 'Warm and energetic orange-red tones',
    config: {
      primaryColor: '#f97316',
      primaryColorLight: '#fb923c',
      primaryColorDark: '#ea580c',
      accentColor: '#06b6d4',
      backgroundColor: '#ffffff',
      textColor: '#1c1917',
      textColorLight: '#78716c',
      bubbleUserBg: '#f97316',
      bubbleAgentBg: '#fff7ed',
      borderRadius: 'soft',
    },
  },
  {
    id: 'rose',
    name: 'Rose',
    description: 'Elegant and friendly pink tones',
    config: {
      primaryColor: '#e11d48',
      primaryColorLight: '#fb7185',
      primaryColorDark: '#be123c',
      accentColor: '#14b8a6',
      backgroundColor: '#ffffff',
      textColor: '#1f2937',
      textColorLight: '#6b7280',
      bubbleUserBg: '#e11d48',
      bubbleAgentBg: '#fff1f2',
      borderRadius: 'rounded',
    },
  },
  {
    id: 'purple',
    name: 'Royal Purple',
    description: 'Rich and creative purple palette',
    config: {
      primaryColor: '#8b5cf6',
      primaryColorLight: '#a78bfa',
      primaryColorDark: '#7c3aed',
      accentColor: '#22c55e',
      backgroundColor: '#ffffff',
      textColor: '#1e1b4b',
      textColorLight: '#6366f1',
      bubbleUserBg: '#8b5cf6',
      bubbleAgentBg: '#f5f3ff',
      borderRadius: 'rounded',
    },
  },
  {
    id: 'slate',
    name: 'Slate',
    description: 'Clean and minimal monochrome style',
    config: {
      primaryColor: '#475569',
      primaryColorLight: '#64748b',
      primaryColorDark: '#334155',
      accentColor: '#3b82f6',
      backgroundColor: '#ffffff',
      textColor: '#0f172a',
      textColorLight: '#64748b',
      bubbleUserBg: '#475569',
      bubbleAgentBg: '#f1f5f9',
      borderRadius: 'soft',
    },
  },
  {
    id: 'midnight',
    name: 'Midnight',
    description: 'Dark mode with deep blue accents',
    config: {
      primaryColor: '#3b82f6',
      primaryColorLight: '#60a5fa',
      primaryColorDark: '#2563eb',
      accentColor: '#f472b6',
      backgroundColor: '#0f172a',
      textColor: '#f1f5f9',
      textColorLight: '#94a3b8',
      bubbleUserBg: '#3b82f6',
      bubbleAgentBg: '#1e293b',
      borderRadius: 'rounded',
    },
  },
];

/**
 * Theme constants for the main application (admin portal)
 * These colors are from useAppTheme.ts
 */
export const APP_THEME_COLORS = {
  light: {
    primary: '#2B17AC',
    primaryLight: '#9685FF',
    primaryDark: '#6552E0',
    secondary: '#A295F9',
    background: '#F9F9FB',
    paper: '#ffffff',
    textPrimary: 'rgba(24, 19, 57, 1.0)',
    textSecondary: 'rgba(0, 0, 0, 0.6)',
  },
  dark: {
    primary: '#9685FF',
    primaryLight: '#9685FF',
    primaryDark: '#6552E0',
    secondary: '#A295F9',
    background: '#0F0E17',
    paper: '#1B1A23',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255, 255, 255, 0.7)',
  },
};
